import type {ValidationResult} from '@github-ui/entity-validators'
import {validateComment} from '@github-ui/entity-validators'
import type {updateIssueCommentBodyMutation_Comment$key} from '@github-ui/issue-body'
import {commitUpdateIssueCommentBodyMutation, updateCommentFragment} from '@github-ui/issue-body'
import {MarkdownEditor, type MarkdownEditorConfig, type Subject} from '@github-ui/markdown-editor'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {testIdProps} from '@github-ui/test-id-props'
import {useToastContext} from '@github-ui/toast'
import type {MarkdownEditorHandle} from '@primer/react/experimental'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {graphql, useRelayEnvironment} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {BUTTON_LABELS, ERRORS, TEST_IDS} from '../../constants'
import type {IssueCommentEditor_repository$key} from './__generated__/IssueCommentEditor_repository.graphql'
import type {IssueCommentViewerCommentRow$key} from './__generated__/IssueCommentViewerCommentRow.graphql'
import {IssueCommentFragment} from './IssueCommentViewer'

type Props = {
  comment: IssueCommentViewerCommentRow$key
  repo: IssueCommentEditor_repository$key
  cancelEdit: () => void
  onSave: () => void
  onChange: () => void
  markdownEditorConfig?: MarkdownEditorConfig
  setIsSubmitting: (isSubmitting: boolean) => void
  isSubmitting: boolean
}

export const IssueCommentEditorFragment = graphql`
  fragment IssueCommentEditor_repository on IssueComment {
    repository {
      slashCommandsEnabled
      nameWithOwner
      databaseId
    }
    ...updateIssueCommentBodyMutation_Comment
    ...deleteIssueCommentMutation_Comment
  }
`

export function IssueCommentEditor({
  comment,
  repo,
  cancelEdit,
  onSave,
  onChange,
  markdownEditorConfig,
  isSubmitting,
  setIsSubmitting,
}: Props) {
  const ref = useRef<MarkdownEditorHandle>(null)
  const {id, body} = useFragment(IssueCommentFragment, comment)
  const commentRepository = useFragment<IssueCommentEditor_repository$key>(IssueCommentEditorFragment, repo)
  const commentData = useFragment<updateIssueCommentBodyMutation_Comment$key>(updateCommentFragment, commentRepository)
  const {repository} = commentRepository

  const repositoryDatabaseId = repository.databaseId!
  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()
  const [currentBody, setCurrentBody] = useState<string>(body)
  const [bodyIsStale, setBodyIsStale] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [originalBody, setOriginalBody] = useState<string | null>(null)
  const [validationResult, setValidationResult] = useState<ValidationResult>(validateComment(currentBody))

  useEffect(() => {
    setValidationResult(validateComment(currentBody))
  }, [currentBody])

  useEffect(() => {
    if (isEditing && !isSubmitting) {
      setBodyIsStale(body !== originalBody)
    }

    return () => {
      setBodyIsStale(false)
    }
  }, [body, isEditing, isSubmitting, originalBody])

  const onMarkdownChange = (newMarkdown: string) => {
    setOriginalBody(current => {
      if (current === null) {
        return body
      }
      return current
    })
    setCurrentBody(newMarkdown)
    setIsEditing(true)
    onChange()
  }

  const save = useCallback(
    (newId: string) => {
      setIsSubmitting(true)
      commitUpdateIssueCommentBodyMutation({
        environment,
        input: {id: newId, body: currentBody},
        onError: () => {
          setIsSubmitting(false)
          addToast({
            type: 'error',
            message: ERRORS.couldNotEditComment,
          })
        },
        onCompleted: () => {
          setIsSubmitting(false)
        },
        commentData,
      })
      onSave()
    },
    [setIsSubmitting, environment, currentBody, commentData, onSave, addToast],
  )

  const onCancel = () => {
    cancelEdit()
  }

  useEffect(() => {
    if (ref && ref.current) {
      ref.current.focus()
    }
  }, [ref])

  const subject = useMemo<Subject>(() => {
    return {
      type: 'issue_comment',
      repository: {
        databaseId: repositoryDatabaseId,
        nwo: repository.nameWithOwner,
        slashCommandsEnabled: repository.slashCommandsEnabled,
      },
    }
  }, [repository.nameWithOwner, repository.slashCommandsEnabled, repositoryDatabaseId])

  return (
    <MarkdownEditor
      disabled={isSubmitting}
      subject={subject}
      ref={ref}
      value={currentBody as SafeHTMLString}
      onSave={() => save(id)}
      onCancel={onCancel}
      validationResult={validationResult}
      onChange={onMarkdownChange}
      saveButtonText={BUTTON_LABELS.updateComment}
      userSettings={markdownEditorConfig}
      saveButtonTrailingIcon={!isSubmitting}
      contentIsStale={bodyIsStale}
      buttonSize="small"
      sx={{border: 'none', py: 0}}
      {...testIdProps(TEST_IDS.markdownEditor('comment-editor'))}
    />
  )
}

try{ IssueCommentEditor.displayName ||= 'IssueCommentEditor' } catch {}